import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Eye, EyeOff, LockKeyhole, Send, UserRound, X } from "lucide-react";
import Logo from "@images/logo.svg";
import { authApi } from "@services/contentApi";
import "@styles/auth.css";

export default function LoginPage({ modal = false, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState("loading");
  const [step, setStep] = useState("credentials");
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ login: "", password: "", passwordConfirmation: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [challengeToken, setChallengeToken] = useState("");
  const [botUsername, setBotUsername] = useState("");

  useEffect(() => {
    authApi.me().then(() => setStatus("authenticated")).catch(() => setStatus("guest"));
  }, []);

  useEffect(() => {
    if (!modal) return undefined;
    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [modal, onClose]);

  useEffect(() => {
    if (step !== "telegram" || !challengeToken) return undefined;
    let active = true;
    let checking = false;
    const check = async () => {
      if (!active || checking) return;
      checking = true;
      try {
        const result = await authApi.botAuthStatus(challengeToken);
        if (result.status === "confirmed") {
          setStatus("authenticated");
          navigate(location.state?.from || (modal ? "/profile" : result.user.role === "admin" ? "/admin" : "/profile"), { replace: true });
        }
      } catch (requestError) {
        if (requestError.response?.data?.status !== "pending") {
          setError(requestError.response?.data?.message || "Не удалось подтвердить аккаунт через Telegram");
        }
      } finally { checking = false; }
    };
    check();
    const timer = window.setInterval(check, 2000);
    return () => { active = false; window.clearInterval(timer); };
  }, [challengeToken, location.state, modal, navigate, step]);

  const updateField = (event) => {
    setForm((current) => ({ ...current, [event.target.name]: event.target.value }));
    setError("");
  };

  const continueToTelegram = async (event) => {
    event.preventDefault();
    if (!/^[a-zA-Z0-9._-]{3,32}$/.test(form.login.trim())) {
      setError("Введите логин: 3–32 латинских буквы, цифры или символы . _ -");
      return;
    }
    if (form.password.length < 8) {
      setError("Пароль должен содержать минимум 8 символов");
      return;
    }
    if (mode === "register" && form.password !== form.passwordConfirmation) {
      setError("Пароли не совпадают");
      return;
    }
    setError("");
    setStatus("authorizing");
    try {
      const result = await authApi.startBotAuth({ ...form, mode });
      setChallengeToken(result.token);
      setBotUsername(result.bot?.username || "");
      setStep("telegram");
      setStatus("guest");
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Не удалось начать подтверждение");
      setStatus("guest");
    }
  };

  if (status === "authenticated") return <Navigate to={modal ? "/profile" : location.state?.from || "/profile"} replace />;

  return (
    <main
      className={`auth-page ${modal ? "auth-modal" : ""}`}
      role={modal ? "dialog" : undefined}
      aria-modal={modal ? "true" : undefined}
      onMouseDown={(event) => {
        if (modal && event.target === event.currentTarget) onClose?.();
      }}
    >
      <div className="auth-ambient auth-ambient-one" />
      <div className="auth-ambient auth-ambient-two" />
      <section className="auth-card" aria-labelledby="auth-title">
        {modal && (
          <button className="auth-close" type="button" onClick={onClose} aria-label="Закрыть окно входа">
            <X size={20} />
          </button>
        )}
        <img className="auth-logo" src={Logo} alt="Смотри" />
        <div className="auth-progress" aria-label="Шаг авторизации">
          <span className="is-active"><Check size={13} /></span>
          <i className={step === "telegram" ? "is-active" : ""} />
          <span className={step === "telegram" ? "is-active" : ""}>2</span>
        </div>

        {step === "credentials" ? (
          <form onSubmit={continueToTelegram} className="auth-form">
            <div className="auth-mode-switch" aria-label="Режим авторизации">
              <button type="button" className={mode === "login" ? "is-active" : ""} onClick={() => { setMode("login"); setForm((current) => ({ ...current, passwordConfirmation: "" })); setError(""); }}>Войти</button>
              <button type="button" className={mode === "register" ? "is-active" : ""} onClick={() => { setMode("register"); setError(""); }}>Создать аккаунт</button>
            </div>
            <div className="auth-heading">
              <span className="auth-kicker">Личный кабинет</span>
              <h1 id="auth-title">{mode === "login" ? "Вход в аккаунт" : "Новый аккаунт"}</h1>
              <p>{mode === "login" ? "Введите логин и пароль существующего аккаунта." : "Придумайте логин и пароль, затем подтвердите регистрацию."}</p>
            </div>

            <label className="auth-field">
              <span>Логин</span>
              <div className="auth-input-wrap">
                <UserRound size={19} />
                <input name="login" value={form.login} onChange={updateField} autoComplete="username" placeholder="Ваш логин" autoFocus />
              </div>
            </label>

            <label className="auth-field">
              <span>Пароль</span>
              <div className="auth-input-wrap">
                <LockKeyhole size={19} />
                <input name="password" type={showPassword ? "text" : "password"} value={form.password} onChange={updateField} autoComplete={mode === "login" ? "current-password" : "new-password"} placeholder="Не менее 8 символов" />
                <button type="button" className="auth-eye" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? "Скрыть пароль" : "Показать пароль"}>
                  {showPassword ? <EyeOff size={19} /> : <Eye size={19} />}
                </button>
              </div>
            </label>

            {mode === "register" && form.password && (
              <label className="auth-field auth-confirm-field">
                <span>Подтвердите пароль</span>
                <div className={`auth-input-wrap ${form.passwordConfirmation && form.password === form.passwordConfirmation ? "is-valid" : ""}`}>
                  <LockKeyhole size={19} />
                  <input name="passwordConfirmation" type={showPassword ? "text" : "password"} value={form.passwordConfirmation} onChange={updateField} autoComplete="new-password" placeholder="Повторите пароль" />
                  {form.passwordConfirmation && form.password === form.passwordConfirmation && <Check className="auth-valid-icon" size={19} />}
                </div>
              </label>
            )}

            {error && <div className="auth-error" role="alert">{error}</div>}
            <button className="auth-submit" type="submit" disabled={status === "loading" || status === "authorizing"}>
              {status === "loading" ? "Проверяем сессию…" : status === "authorizing" ? "Создаём подтверждение…" : <>Далее <ArrowRight size={19} /></>}
            </button>
          </form>
        ) : (
          <div className="auth-telegram-step">
            <button className="auth-back" type="button" onClick={() => { setStep("credentials"); setChallengeToken(""); setError(""); }} disabled={status === "authorizing"}>
              <ArrowLeft size={17} /> Назад
            </button>
            <div className="auth-telegram-icon"><Send size={28} /></div>
            <div className="auth-heading">
              <span className="auth-kicker">Последний шаг</span>
              <h1 id="auth-title">Подтвердите через Telegram</h1>
              <p>Telegram нужен, чтобы безопасно подтвердить, что аккаунт принадлежит вам.</p>
            </div>
            <div className="auth-account-preview"><UserRound size={17} /><span>{form.login}</span></div>
            {!botUsername ? (
              <div className="auth-error">Сервер не смог проверить username Telegram-бота.</div>
            ) : (
              <a className="auth-telegram-open" href={`https://t.me/${botUsername.replace(/^@/, "")}?start=${challengeToken}`} target="_blank" rel="noreferrer"><Send size={18} /> Открыть Telegram-бота</a>
            )}
            <span className="auth-status">Нажмите Start в боте. Эта страница подтвердит вход автоматически.</span>
            {error && <div className="auth-error" role="alert">{error}</div>}
            <p className="auth-privacy"><LockKeyhole size={14} /> Мы не получаем доступ к вашим сообщениям.</p>
          </div>
        )}
      </section>
    </main>
  );
}
